import { css } from "@emotion/css";
import { space } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusQuery, useMutation } from "@octopusdeploy/octopus-react-client";
import type { ArgoCDGatewayResource, Repository } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import type { Moment } from "moment";
import moment from "moment/moment";
import React, { useMemo, useState } from "react";
import { ArgoCDGatewayHealthCallout } from "~/areas/infrastructure/components/ArgoCDInstances/components/ArgoCDGatewayHealthCallout";
import { KubernetesPodLogs } from "~/components/KubernetesPodLogsTable/KubernetesPodLogs";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import { createErrorInfoFromError } from "~/components/PageContent/createErrorInfo";
import { DataTable, DataTableBody, DataTableRow, DataTableRowColumn, DataTableRowHeaderColumn } from "~/primitiveComponents/dataDisplay/DataTable/index";
import SectionHeading from "~/primitiveComponents/dataDisplay/Section/SectionHeading";
import type { ArgoCDInstancePageLoaderData } from "./ArgoCDInstancePageLoaderData";
const styles = {
    alignLeft: css({
        "&&": {
            textAlign: "left",
            paddingLeft: space[16],
        },
    }),
    tableWrapper: css({
        paddingTop: space[16],
    }),
};
type ArgoCDInstanceConnectivityPageProps = {
    loaderData: ArgoCDInstanceConnectivityPageLoaderData;
};
export type ArgoCDInstanceConnectivityPageLoaderData = ArgoCDInstancePageLoaderData;
export async function argoCDInstanceConnectivityPageLoader(_: Repository, gatewayPromise: Promise<ArgoCDGatewayResource>): Promise<ArgoCDInstanceConnectivityPageLoaderData> {
    const gateway = await gatewayPromise;
    return { gateway };
}
export const ArgoCDInstanceConnectivityPage = ({ loaderData }: ArgoCDInstanceConnectivityPageProps) => {
    const { gateway } = loaderData;
    const { navigate } = useSpaceAwareNavigation();
    const [requestCompletedTime, setRequestCompletedTime] = useState<Moment>(moment());
    const { result: podLogs, isLoading: isLoadingLogs, error: logsError, refetch: refreshLogs, } = useInlineStatusQuery(async (repo) => {
        const logLines = await repo.ArgoCDGateways.getPodLogs(gateway.Id);
        setRequestCompletedTime(moment());
        return logLines;
    }, [gateway], "Load Argo CD Gateway pod logs");
    const { execute: createHealthCheckTask, isExecuting: isCreatingHealthCheckTask } = useMutation({
        name: "Create ArgoCDGatewayHealthCheck task",
        action: (repo) => repo.Tasks.createHealthCheckTaskForArgoCDGateway(gateway),
        afterComplete: async (_, createdTask) => {
            navigate(links.taskPage.generateUrl({ taskId: createdTask.Id }));
        },
    });
    const dataRows = useMemo(() => {
        if (!gateway.InstallDetails) {
            return [];
        }
        return [
            ["Gateway Version", gateway.InstallDetails.GatewayVersion],
            ["Helm Chart Version", gateway.InstallDetails.HelmChartVersion],
            ["Helm Release Name", gateway.InstallDetails.HelmReleaseName],
            ["Namespace", gateway.InstallDetails.KubernetesNamespace],
            ["Argo CD Version", gateway.InstallDetails.ArgoCDVersion ?? "Unknown"],
        ];
    }, [gateway]);
    const logsErrorInfo = useMemo(() => logsError && createErrorInfoFromError(logsError), [logsError]);
    return (<PageContent header={{
            title: "Gateway Connectivity",
            primaryAction: {
                type: "button",
                label: "Check health",
                onClick: createHealthCheckTask,
                disabled: isCreatingHealthCheckTask,
            },
        }}>
            <ArgoCDGatewayHealthCallout gateway={gateway}/>
            {gateway.InstallDetails && (<section>
                    <SectionHeading title={"Details"}/>
                    <DataTable>
                        <DataTableBody>
                            {dataRows.map(([header, value]) => (<DataTableRow key={header}>
                                    <DataTableRowHeaderColumn className={styles.alignLeft}>{header}</DataTableRowHeaderColumn>
                                    <DataTableRowColumn>{value}</DataTableRowColumn>
                                </DataTableRow>))}
                        </DataTableBody>
                    </DataTable>
                </section>)}
            <section>
                <SectionHeading title={"Gateway Pod Logs"}/>
                <div className={styles.tableWrapper}>
                    <KubernetesPodLogs logLines={podLogs} isLoading={isLoadingLogs} errorInfo={logsErrorInfo} localStoragePrefix={"Octopus.Infrastructure.ArgoCDInstances.GatewayConnectivity"} lastRefreshed={requestCompletedTime} refresh={refreshLogs} backgroundColor={"primary"}/>
                </div>
            </section>
        </PageContent>);
};
